import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import { pageByIdQueryOptions } from "@/api-queries/pages";
import { BlogEditor } from "@/components/blog/BlogEditor";
import { PageTranslateButton, type PageTranslationResult } from "@/components/admin/pages/PageTranslateButton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  CaretLeft,
  CircleNotch,
  FloppyDisk,
  PaperPlaneTilt,
  Image as ImageIcon,
  Eye,
  EyeSlash,
  Globe,
  Translate,
  Plus,
} from "@phosphor-icons/react";
import { MediaPicker } from "@/components/ui/media-picker";
import { toast } from "@/lib/toast";
import { throwIfNotOk } from "@/lib/api-error";
import { countWords } from "@/lib/content-helpers";
import { LOCALES, LOCALE_MAP } from "@/lib/locale";

export const Route = createFileRoute("/dashboard/admin/pages/$pageId/edit")({
  component: AdminEditPage,
});


function AdminEditPage() {
  const { pageId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const {
    data: page,
    isLoading,
    error,
  } = useQuery(pageByIdQueryOptions(pageId));

  const [title, setTitle] = useState("");
  const [content, setContent] = useState<object | null>(null);
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [editorKey, setEditorKey] = useState(0);

  if (page && !initialized) {
    setTitle(page.title || "");
    setCoverImageUrl(page.coverImageUrl || "");
    try {
      setContent(
        typeof page.content === "string"
          ? JSON.parse(page.content)
          : page.content
      );
    } catch {
      setContent(null);
    }
    setInitialized(true);
  }

  const handleContentChange = useCallback((newContent: object) => {
    setContent(newContent);
  }, []);

  const handleTranslated = useCallback((translation: PageTranslationResult) => {
    setTitle(translation.title);
    setContent(translation.content);
    if (!coverImageUrl && translation.coverImageUrl) {
      setCoverImageUrl(translation.coverImageUrl);
    }
    setEditorKey((key) => key + 1);
  }, [coverImageUrl]);

  const canSubmit = title.trim().length > 0 && content !== null;

  const handleSubmit = async (status: "draft" | "published") => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const res = await fetch(`/api/pages/${pageId}/update`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          content: JSON.stringify(content),
          // null persists a removed cover; undefined would keep the old one.
          coverImageUrl: coverImageUrl || null,
          status,
        }),
      });
      await throwIfNotOk(res, "Greška pri ažuriranju stranice.");
      await queryClient.invalidateQueries({ queryKey: ["pages"] });
      navigate({ to: "/dashboard/admin/pages" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Greška pri ažuriranju stranice.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const wordCount = content ? countWords(content) : 0;

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <div className="mb-3 h-4 w-32 animate-pulse rounded bg-muted" />
          <div className="h-10 w-96 animate-pulse rounded bg-muted" />
          <div className="mt-3 h-4 w-48 animate-pulse rounded bg-muted/60" />
        </div>
        <div className="h-px bg-border" />
        <div className="h-96 animate-pulse rounded-xl bg-muted/20" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="mx-auto max-w-7xl">
        <Link
          to="/dashboard/admin/pages"
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <CaretLeft className="size-4" />
          Nazad na stranice
        </Link>
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/60 bg-muted/20 px-6 py-16 text-center">
          <h3
            className="text-lg font-semibold text-foreground"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Stranica nije pronađena
          </h3>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Stranica koju tražite ne postoji ili je obrisana.
          </p>
        </div>
      </div>
    );
  }

  const localeInfo = LOCALE_MAP[page.locale] || { label: page.locale, flag: "" };
  const translations = (page.translations || []) as { id: string; locale: string; title: string }[];
  const missingLocales = ["bs", "en"].filter(
    (l) => l !== page.locale && !translations.some((t) => t.locale === l)
  );
  const bosnianSource = page.locale === "en"
    ? translations.find((t) => t.locale === "bs")
    : null;

  return (
    <div className="mx-auto max-w-7xl">
      {/* Top bar */}
      <div className="mb-6 flex items-center justify-between sm:mb-8">
        <Link
          to="/dashboard/admin/pages"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <CaretLeft className="size-4" />
          Stranice
        </Link>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden md:inline-flex"
            title={sidebarOpen ? "Sakrij panel" : "Prikaži panel"}
          >
            {sidebarOpen ? (
              <EyeSlash className="size-4" />
            ) : (
              <Eye className="size-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSubmit("draft")}
            disabled={isSubmitting || !canSubmit}
          >
            {isSubmitting ? (
              <CircleNotch className="size-3.5 animate-spin" />
            ) : (
              <FloppyDisk className="size-3.5" />
            )}
            Nacrt
          </Button>
          <Button
            size="sm"
            onClick={() => handleSubmit("published")}
            disabled={isSubmitting || !canSubmit}
          >
            {isSubmitting ? (
              <CircleNotch className="size-3.5 animate-spin" />
            ) : (
              <PaperPlaneTilt className="size-3.5" />
            )}
            Objavi
          </Button>
        </div>
      </div>

      <div className="flex gap-8">
        {/* Main editor area */}
        <div className="min-w-0 flex-1">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Naslov stranice..."
            className="w-full border-none bg-transparent text-3xl font-bold tracking-tight text-foreground outline-none placeholder:text-muted-foreground/30 sm:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          />

          <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
            <span>{wordCount} riječi</span>
            <span>~{Math.max(1, Math.ceil(wordCount / 200))} min čitanja</span>
          </div>

          <div className="my-6 h-px bg-border" />

          <div className="[&_.rounded-lg]:rounded-none [&_.rounded-lg]:border-x-0 [&_.rounded-lg]:border-t-0 [&_.ProseMirror]:min-h-[500px] [&_.ProseMirror]:px-0">
            {initialized && (
              <BlogEditor
                key={editorKey}
                initialContent={content || undefined}
                onChange={handleContentChange}
              />
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div
          className={cn(
            "hidden shrink-0 transition-all duration-300 md:block",
            sidebarOpen ? "w-72 opacity-100" : "w-0 overflow-hidden opacity-0"
          )}
        >
          <div className="sticky top-6 space-y-5">
            {/* Language info */}
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Globe className="size-3.5" />
                Jezik
              </p>
              <div className="rounded-lg bg-muted/60 px-3 py-2 text-sm font-medium">
                {localeInfo.flag} {localeInfo.label}
              </div>
            </div>

            {/* Translations */}
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Translate className="size-3.5" />
                Prijevodi
              </p>
              {translations.length > 0 ? (
                <div className="space-y-1.5">
                  {translations.map((t) => {
                    const tLocale = LOCALE_MAP[t.locale] || { label: t.locale, flag: "" };
                    return (
                      <Link
                        key={t.id}
                        to={`/dashboard/admin/pages/${t.id}/edit`}
                        className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2 text-sm transition-colors hover:bg-muted"
                      >
                        <span>{tLocale.flag}</span>
                        <span className="truncate">{t.title}</span>
                      </Link>
                    );
                  })}
                </div>
              ) : null}
              {missingLocales.map((l) => {
                const lInfo = LOCALE_MAP[l] || { label: l, flag: "" };
                return (
                  <Link
                    key={l}
                    to="/dashboard/admin/pages/new"
                    search={{ groupId: page.groupId, locale: l }}
                    className="flex items-center gap-1.5 rounded-lg border border-dashed border-border/60 px-3 py-2 text-sm text-primary transition-colors hover:bg-primary/5"
                  >
                    <Plus className="size-3.5" />
                    Dodaj {lInfo.flag} {lInfo.label}
                  </Link>
                );
              })}
            </div>

            {bosnianSource && (
              <div className="space-y-2">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <Translate className="size-3.5" />
                  AI prijevod
                </p>
                <PageTranslateButton
                  sourcePageId={bosnianSource.id}
                  disabled={isSubmitting}
                  label="Prevedi iz BiH verzije"
                  onTranslated={handleTranslated}
                />
              </div>
            )}

            {/* Cover image */}
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <ImageIcon className="size-3.5" />
                Naslovna slika
              </p>
              <MediaPicker
                value={coverImageUrl}
                onChange={setCoverImageUrl}
                folder="pages"
                hint="JPG, PNG ili WebP · maks. 5 MB"
                previewHeight="h-52 sm:h-64"
              />
            </div>

            <div className="rounded-xl border bg-muted/20 p-3.5">
              <p className="text-xs leading-relaxed text-muted-foreground">
                <strong className="text-foreground">Nacrt</strong> sprema
                stranicu bez objavljivanja.{" "}
                <strong className="text-foreground">Objavi</strong> je odmah
                stavlja na platformu.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sidebar */}
      <div className="mt-8 space-y-5 md:hidden">
        <div className="h-px bg-border" />

        {/* Mobile language info */}
        <div className="space-y-2">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Globe className="size-3.5" />
            Jezik
          </p>
          <div className="rounded-lg bg-muted/60 px-3 py-2 text-sm font-medium">
            {localeInfo.flag} {localeInfo.label}
          </div>
        </div>

        {bosnianSource && (
          <div className="space-y-2">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Translate className="size-3.5" />
              AI prijevod
            </p>
            <PageTranslateButton
              sourcePageId={bosnianSource.id}
              disabled={isSubmitting}
              label="Prevedi iz BiH verzije"
              onTranslated={handleTranslated}
            />
          </div>
        )}

        <div className="space-y-2">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <ImageIcon className="size-3.5" />
            Naslovna slika
          </p>
          <MediaPicker
            value={coverImageUrl}
            onChange={setCoverImageUrl}
            folder="pages"
            hint="JPG, PNG ili WebP · maks. 5 MB"
            previewHeight="h-52 sm:h-64"
          />
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleSubmit("draft")}
            disabled={isSubmitting || !canSubmit}
            className="flex-1"
          >
            <FloppyDisk className="size-4" />
            Nacrt
          </Button>
          <Button
            onClick={() => handleSubmit("published")}
            disabled={isSubmitting || !canSubmit}
            className="flex-1"
          >
            <PaperPlaneTilt className="size-4" />
            Objavi
          </Button>
        </div>
      </div>
    </div>
  );
}
